import { ReadonlyURLSearchParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import dayjs from "dayjs";
import isLeapYear from 'dayjs/plugin/isLeapYear';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/ko';
import { COMMERCIAL_SPACE_TYPE, EXTERIOR_SPACE_TYPE, INTERIOR_SPACE_TYPE, REIMAGINE_SPACE_TYPE } from "@/consts/consts";
import { saveAs } from 'file-saver';
import { SupportedLanguage } from "@/config/store/globalStore";

dayjs.extend(isLeapYear); // 플러그인 등록
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.locale('ko'); // 언어 등록

export const dateToString = (date: Date | number | string, format: string = 'YYYY-MM-DD') => dayjs.utc(date).local().format(format)

export const timeForToday = (date: Date | string, detailDiff: boolean = true) => {
  const localDate = dayjs.utc(date).local();

  if (!detailDiff) {
    const timeDiffDuration = dayjs.duration(dayjs().diff(localDate));
    const dayDiff = parseInt(timeDiffDuration.format('D'));
    if (dayDiff == 0) {
      return '오늘';
    }
  }
  
  return localDate.fromNow();
};

export const sleep = (ms: number) => {
  return new Promise((r) => setTimeout(r, ms));
};

export const getPagination = (pageNum: number, dataPerPage=10) => {
  const startIndex = (pageNum-1) * dataPerPage
  const endIndex = pageNum * dataPerPage
  return { startIndex, endIndex }
}

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'instant' })
}

export const useReplaceQueryParams = () => {
  const router = useRouter();

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const queryParams = searchParams as ReadonlyURLSearchParams;
  const urlSearchParams = new URLSearchParams(searchParams + '');

  function setQueryParams(params: any) {
    const search = params + '';
    router.push(`${pathname}?${search}`, {
      scroll: false,
    });
  }

  return { queryParams, urlSearchParams, setQueryParams };
}


export const extractNaverPlaceUrl = (text: string) => {
  const urlRegex = /(https:\/\/\S+)/;
  const matches = text.match(urlRegex);

  if (matches && matches.length > 0) {
    return matches[0]
  }
  return text
}


export function saveBase64Image(base64Data: string, fileName: string) {
  // 변환된 base64 데이터를 Blob으로 만듭니다.
  const byteCharacters = atob(base64Data.split(',')[1]);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: 'image/png' });

  // 브라우저에서 다운로드할 수 있는 링크를 생성합니다.
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;

  // 링크를 클릭하여 다운로드를 시작합니다.
  document.body.appendChild(link);
  link.click();

  // 사용이 끝나면 링크를 제거합니다.
  document.body.removeChild(link);
}


export async function downloadImage(imageSrc: string, filename: string='result.png') {
  await fetch(imageSrc)
  .then(response => response.blob())
  .then(blob => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  })
}

export async function imageBufferToBase64(imgFile: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64data = reader.result as string;
      resolve(base64data);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(imgFile);
  });
}


export const shortenAddress = (address: string, language: SupportedLanguage) => {
  if (language === 'ko') {
    return address.split(' ').slice(0, 2).join(' ');
  } else if (language === 'en') {
    const parts = address.split(',').map(part => part.trim());
    return parts.slice(-2).join(', ');
  } else if (language === 'ja') {
    const index = address.indexOf('区');
    return address.slice(0, index + 1);
  } else if (language === 'zh') {
    const index = address.indexOf('区');
    return address.slice(0, index + 1);
  }
};